import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { selectUser, updateMenuAccess } from "../../store/auth/authSlice";

const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("cms_token")}` });

const normalize = (menus) =>
  Array.isArray(menus)
    ? menus.map((menu) => ({
        ...menu,
        id: menu.cms_menu_id,
        sub_menus: Array.isArray(menu.sub_menus)
          ? menu.sub_menus.map((sub) => ({ ...sub, id: sub.cms_sub_menu_id }))
          : [],
      }))
    : [];

/**
 * useCmsMenus
 *
 * - After login : shows menu_access from the login response stored in Redux state (instant, no delay)
 * - After refresh: calls GET /cms-roles/{cms_role_id} to get latest menu_access from API
 *   and replaces the Redux-based data once the API responds.
 *
 * No localStorage "cms_menu_access" key is used.
 */
const useCmsMenus = () => {
  const dispatch = useDispatch();
  // Full login response is stored in Redux state by loginSuccess
  const authUser = useSelector(selectUser);
  const cmsRoleId = authUser?.user?.cms_role_id;

  // Immediate data from login response (available instantly after login or from persisted Redux)
  const loginMenus = useMemo(
    () => normalize(authUser?.menu_access || []),
    [authUser]
  );

  // Fresh data from API (replaces loginMenus once loaded)
  const [freshMenus, setFreshMenus] = useState(null);

  useEffect(() => {
    if (!cmsRoleId) return;
    axios
      .get(`${API_BASE_URL}/cms-roles/${cmsRoleId}`, { headers: authH() })
      .then((res) => {
        const menus = res.data?.data?.menu_access || [];
        setFreshMenus(normalize(menus));
        // Keep Redux in sync so usePermissions always reads fresh data
        dispatch(updateMenuAccess(menus));
      })
      .catch(() => { /* keep showing loginMenus on error */ });
  }, [cmsRoleId]);

  return { data: freshMenus ?? loginMenus };
};

export default useCmsMenus;
